'use es6';

import { Fragment, useCallback } from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import * as CustomPropTypes from '../../../../constants/PropTypes';
import FormattedList from 'I18n/components/FormattedList';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import UILink from 'UIComponents/link/UILink';
import UIDescriptionList from 'UIComponents/list/UIDescriptionList';
import Pricing from 'marketplace-ui-common/records/Pricing';
import IntegrationFeature from 'ecosystem-marketplace-core/records/IntegrationFeature';
import Scope from 'developer-experience-shared-components/scopes/models/Scope';
import { getFormattedUrl } from '../../../../utils/urls';
import RequiredPermissions from '../../permissions/RequiredPermissions';
import PlanCompatibility from './PlanCompatibility';
import { TAB_IDS } from '../../Header';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getI18nKey = key => `appMarketplace.detail.overview.sidebar.requirements.${key}`;
const Section = ({
  children,
  label
}) => /*#__PURE__*/_jsxs(Fragment, {
  children: [/*#__PURE__*/_jsx("dt", {
    children: label
  }), /*#__PURE__*/_jsx("dd", {
    children: children
  })]
});
const Requirements = ({
  appName,
  companyName,
  connectionType,
  homemade,
  iconUrl,
  installability,
  integrationFeatures,
  isCustomer,
  isOpsHubListing,
  neededScopeGroups,
  onOpenFeatureCompatibilityModal,
  onTrackInteraction,
  pricingUrl: appPricingUrl,
  pricings,
  scrollToTabSection,
  tiers
}) => {
  const trackInteraction = useCallback((action, value) => onTrackInteraction(action, 'requirements', value), [onTrackInteraction]);
  const renderedAppPricingPlans = /*#__PURE__*/_jsx(UILink, {
    external: !!appPricingUrl,
    href: getFormattedUrl(appPricingUrl),
    onClick: () => {
      trackInteraction('click app pricing link');
      if (!appPricingUrl) {
        scrollToTabSection(TAB_IDS.PRICING);
      }
    },
    children: /*#__PURE__*/_jsx(FormattedReactMessage, {
      message: getI18nKey('pricingPlans'),
      options: {
        count: pricings.size,
        plans: /*#__PURE__*/_jsx(FormattedList, {
          exclusive: true,
          children: pricings.map((pricing, index) => /*#__PURE__*/_jsx("span", {
            children: pricing.get('name')
          }, `pricing-${index}`))
        })
      }
    })
  });
  return /*#__PURE__*/_jsxs(UIDescriptionList, {
    children: [!tiers.isEmpty() && /*#__PURE__*/_jsx(Section, {
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: getI18nKey('compatibilityHeadingText')
      }),
      children: /*#__PURE__*/_jsx(PlanCompatibility, {
        integrationFeatures: integrationFeatures,
        installability: installability,
        isOpsHubListing: isOpsHubListing,
        isCustomer: isCustomer,
        onOpenFeatureCompatibilityModal: onOpenFeatureCompatibilityModal,
        tiers: tiers,
        trackInteraction: trackInteraction
      })
    }), /*#__PURE__*/_jsx(Section, {
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: getI18nKey('permissions.headingText')
      }),
      children: /*#__PURE__*/_jsx(RequiredPermissions, {
        appName: appName,
        connectionType: connectionType,
        homemade: homemade,
        iconUrl: iconUrl,
        neededScopeGroups: neededScopeGroups,
        trackInteraction: trackInteraction
      })
    }), !pricings.isEmpty() && /*#__PURE__*/_jsx(Section, {
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: getI18nKey('pricingPlansHeadingText'),
        options: {
          name: homemade ? appName : companyName
        }
      }),
      children: renderedAppPricingPlans
    })]
  });
};
Requirements.propTypes = {
  appName: PropTypes.string.isRequired,
  companyName: PropTypes.string.isRequired,
  connectionType: CustomPropTypes.connectionType.isRequired,
  homemade: PropTypes.bool.isRequired,
  iconUrl: PropTypes.string.isRequired,
  installability: CustomPropTypes.installability,
  integrationFeatures: ImmutablePropTypes.listOf(PropTypes.instanceOf(IntegrationFeature)),
  isCustomer: PropTypes.bool.isRequired,
  isOpsHubListing: PropTypes.bool,
  neededScopeGroups: ImmutablePropTypes.listOf(PropTypes.instanceOf(Scope)).isRequired,
  onOpenFeatureCompatibilityModal: PropTypes.func.isRequired,
  onTrackInteraction: PropTypes.func.isRequired,
  pricingUrl: PropTypes.string,
  pricings: ImmutablePropTypes.listOf(PropTypes.instanceOf(Pricing)).isRequired,
  scrollToTabSection: PropTypes.func.isRequired,
  tiers: ImmutablePropTypes.set.isRequired
};
export default Requirements;